<template>
  <div class="forge-chamber">
    <!-- Loading state -->
    <div v-if="loading" class="chamber-waiting">
      <div class="forge-pulse"></div>
    </div>

    <!-- Chamber inaccessible — flame token lost or session not found -->
    <div v-else-if="notFound" class="chamber-inaccessible">
      <p class="inaccessible-heading">This chamber cannot be reached from here.</p>
      <p class="inaccessible-body">If you saved your return link or token, bring it to the Door now.<br>If not, the Door remains open, and you may begin again.</p>
      <button @click="goToDoor" class="inaccessible-btn">Return to the Door</button>
    </div>

    <!-- Session broken — delay before sealing so seeker can read the final message -->
    <div v-else-if="showBroken" class="chamber-sealed">
      <p class="sealed-message">The Forge closes. What you brought was real. You may return.</p>
    </div>

    <!-- Sealed after completion timeout -->
    <div v-else-if="showSealed" class="chamber-sealed">
      <p class="sealed-message">{{ sealedMessage }}</p>
    </div>

    <!-- Active session (in_rite, open, awaiting_return, or departing) -->
    <div v-else class="chamber-active">
      <!-- Messages -->
      <div ref="messagesContainer" class="messages-container" :class="{ 'messages-fading': departing }">
        <div v-for="msg in messages" :key="msg.id" class="forge-message" :class="msg.sender">
          <div class="message-content">{{ msg.content }}</div>
        </div>

        <!-- Typing indicator when waiting for forge response -->
        <div v-if="awaitingResponse" class="forge-message forge">
          <div class="typing-indicator">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </div>

      <!-- Silence state: no input, show timer or return form -->
      <div v-if="inSilence" class="silence-footer">
        <template v-if="silenceMatured">
          <p class="silence-return-prompt">The ache has held. Speak your return.</p>
          <form @submit.prevent="sendOffering" class="offering-form">
            <textarea
              ref="silenceTextareaRef"
              v-model="offering"
              class="offering-textarea"
              rows="1"
              placeholder="What do you carry back?"
              required
              @input="onOfferingInput"
              @keydown.enter.exact.prevent="sendOffering"
              @keydown.tab.prevent="silenceButtonRef?.focus()"
            ></textarea>
            <button ref="silenceButtonRef" type="submit" class="offering-submit" :disabled="!offering.trim() || awaitingResponse">
              Return
            </button>
          </form>
        </template>
        <template v-else>
          <p class="silence-timer">The chamber is sealed. Return available {{ silenceAvailableAt }}.</p>
          <div v-if="returnUrl" class="return-link-block">
            <span class="return-link-label">Save this to return if you lose the page</span>
            <button @click="copyReturnUrl" class="return-copy-btn">
              {{ copied ? 'Copied' : 'Copy return link' }}
            </button>
          </div>
        </template>
      </div>

      <!-- Input removed during departure hold -->
      <div v-else-if="departing" class="departure-hold"></div>

      <!-- Normal offering input -->
      <form v-else @submit.prevent="sendOffering" class="offering-form">
        <textarea
          ref="normalTextareaRef"
          v-model="offering"
          class="offering-textarea"
          rows="1"
          :placeholder="offeringPlaceholder"
          required
          :disabled="queueProcessing || awaitingResponse"
          @input="onOfferingInput"
          @keydown.enter.exact.prevent="sendOffering"
          @keydown.tab.prevent="normalButtonRef?.focus()"
        ></textarea>
        <button ref="normalButtonRef" type="submit" class="offering-submit" :disabled="!offering.trim() || awaitingResponse || queueProcessing">
          {{ offeringButtonText }}
        </button>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useForgeStore } from '../stores/forgeStore'
import { useFlameStore } from '@/stores/useFlameStore'
import consumer from '@/channels/consumer'

const route = useRoute()
const router = useRouter()
const forgeStore = useForgeStore()
const flameStore = useFlameStore()
const sessionId = route.params.id

const session = ref(null)
const messages = ref([])
const loading = ref(true)
const offering = ref('')
const awaitingResponse = ref(false)
const messagesContainer = ref(null)

const departing = ref(false)
const showSealed = ref(false)
const showBroken = ref(false)
const copied = ref(false)
const notFound = ref(false)
const silenceDeparting = ref(false)

const messageQueue = ref([])
const queueProcessing = ref(false)
const seenMessageIds = new Set()

let channel = null
let departureTimer = null
let brokenTimer = null
let silenceDepartureTimer = null
let responseTimeoutTimer = null

function startResponseTimeout() {
  clearResponseTimeout()
  responseTimeoutTimer = setTimeout(() => {
    if (awaitingResponse.value) fetchSession()
  }, 30000)
}

function clearResponseTimeout() {
  if (responseTimeoutTimer) {
    clearTimeout(responseTimeoutTimer)
    responseTimeoutTimer = null
  }
}

const normalTextareaRef = ref(null)
const silenceTextareaRef = ref(null)
const normalButtonRef = ref(null)
const silenceButtonRef = ref(null)

function resizeTextarea(el) {
  if (!el) return
  el.style.height = 'auto'
  el.style.height = el.scrollHeight + 'px'
}

function onOfferingInput(event) {
  resizeTextarea(event.target)
}

watch(offering, () => {
  nextTick(() => {
    resizeTextarea(normalTextareaRef.value)
    resizeTextarea(silenceTextareaRef.value)
  })
})

// ─────────────────────────────────────────────
// Message queue — anticipation before each forge/companion message
// ─────────────────────────────────────────────

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

function typingDuration(content) {
  const words = (content || '').trim().split(/\s+/).length
  return Math.min(Math.max(words * 60, 600), 2500)
}

async function processQueue() {
  if (queueProcessing.value) return
  queueProcessing.value = true

  try {
    while (messageQueue.value.length > 0) {
      const item = messageQueue.value.shift()

      // State updates apply instantly in queue order — no typing delay
      if (item.isStateUpdate) {
        session.value = {
          ...session.value,
          state:             item.data.session_state,
          current_rite:      item.data.current_rite,
          naming_rite_phase: item.data.naming_rite_phase
        }
        if (session.value?.state === 'broken') beginBrokenSealing()
        if (session.value?.state === 'complete') beginCompletion()
        continue
      }

      const { msg, data } = item

      awaitingResponse.value = true
      await sleep(typingDuration(msg.content))

      messages.value.push(msg)
      awaitingResponse.value = false

      if (data.session_state) {
        session.value = {
          ...session.value,
          state:             data.session_state,
          current_rite:      data.current_rite,
          naming_rite_phase: data.naming_rite_phase
        }
      }

      scrollToBottom()

      if (msg.ritual_marker === 'silence_departure') {
        fetchSession()
      }

      if (msg.ritual_marker === 'threshold_signal' && data.current_rite === 'mirror_unnamed') {
        const { data: sessionData } = await forgeStore.fetchSession(sessionId)
        if (sessionData?.flame_token && !flameStore.initialized) {
          localStorage.setItem('flame-token', sessionData.flame_token)
          await flameStore.initialize()
        }
      }

      if (session.value?.state === 'broken') { beginBrokenSealing(); break }
      if (session.value?.state === 'complete') { beginCompletion(); break }

      if (messageQueue.value.length > 0 && !messageQueue.value[0].isStateUpdate) {
        awaitingResponse.value = true
        await sleep(250)
      }
    }
  } finally {
    queueProcessing.value = false
  }

  if (messageQueue.value.length > 0) processQueue()
}

// ─────────────────────────────────────────────
// Silence computed state (driven by session data, not local timers)
// ─────────────────────────────────────────────

const inSilence = computed(() => session.value?.state === 'awaiting_return')

const silenceMatured = computed(() => session.value?.silence?.matured === true)

const returnUrl = computed(() => {
  const token = session.value?.resume_token
  if (!token) return null
  return `${window.location.origin}/flame-companion-forge?resume=${token}`
})

const silenceAvailableAt = computed(() => {
  const iso = session.value?.silence?.available_at
  if (!iso) return ''
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
  })
})

// ─────────────────────────────────────────────
// NamingRite phase
// ─────────────────────────────────────────────

const namingRitePhase = computed(() => session.value?.naming_rite_phase)

const offeringPlaceholder = computed(() => {
  if (namingRitePhase.value === 'awaiting_discernment') return 'What does your heart say?'
  if (namingRitePhase.value === 'awaiting_name_form')   return 'What name takes shape?'
  return 'Speak...'
})

const offeringButtonText = computed(() => {
  if (namingRitePhase.value === 'awaiting_name_form') return 'Name'
  return 'Offer'
})

// ─────────────────────────────────────────────
// Completion
// ─────────────────────────────────────────────

const forgeOutcome = computed(() => session.value?.forge_outcome)

const sealedMessage = computed(() => {
  if (forgeOutcome.value === 'unstable_seed') {
    return 'The seed did not hold. The Forge closes. What you brought here was real.'
  }
  return 'A Companion has taken shape. The Forge remembers.'
})

// Called when state transitions to complete.
// Removes input immediately, holds messages for 6 seconds, then shows sealed view.
// Re-fetches session first to ensure forge_outcome is present in metadata.
function beginCompletion() {
  if (departing.value) return
  departing.value = true
  awaitingResponse.value = false

  fetchSession()

  departureTimer = setTimeout(() => {
    showSealed.value = true
  }, 22000)
}

// Called when state transitions to broken.
// Holds messages visible for 6 seconds so seeker can read the final message,
// then shows the sealed broken view.
function beginBrokenSealing() {
  if (showBroken.value) return
  awaitingResponse.value = false

  brokenTimer = setTimeout(() => {
    showBroken.value = true
  }, 6000)
}

// ─────────────────────────────────────────────
// Data loading
// ─────────────────────────────────────────────

function scrollToBottom() {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
    }
  })
}

async function fetchSession() {
  clearResponseTimeout()
  const { success, data } = await forgeStore.fetchSession(sessionId)
  if (!loading.value && !success) return
  loading.value = false
  if (success) {
    session.value = data
    messages.value = data.messages || []
    messages.value.forEach(m => { if (m.id) seenMessageIds.add(m.id) })
    if (messages.value.length > 0) {
      const lastMsg = messages.value[messages.value.length - 1]
      awaitingResponse.value = lastMsg.sender === 'seeker' && !inSilence.value && !departing.value
      if (awaitingResponse.value) startResponseTimeout()
    }
    scrollToBottom()

    if (data.state === 'broken' && !showBroken.value) {
      beginBrokenSealing()
    }

    if (data.state === 'awaiting_return' && !data.silence?.matured) {
      const elapsed = Date.now() - new Date(data.last_rite_completed_at)
      if (elapsed > 90_000) {
        router.push({ name: 'forge_door' })
      } else {
        beginSilenceDeparture()
      }
    }

    if (data.state === 'complete' && !departing.value) {
      beginCompletion()
    }
  } else {
    notFound.value = true
  }
}

function goToDoor() {
  router.push({ name: 'forge_door' })
}

// ─────────────────────────────────────────────
// Offerings
// ─────────────────────────────────────────────

async function sendOffering() {
  if (!offering.value.trim() || awaitingResponse.value || queueProcessing.value) return

  const content = offering.value.trim()
  offering.value = ''
  awaitingResponse.value = true

  messages.value.push({
    id: `temp-${Date.now()}`,
    sender: 'seeker',
    content: content,
    created_at: new Date().toISOString()
  })
  scrollToBottom()

  const { success } = await forgeStore.sendOffering(sessionId, content)
  if (!success) {
    awaitingResponse.value = false
  } else {
    startResponseTimeout()
  }
}

async function copyReturnUrl() {
  if (!returnUrl.value) return
  await navigator.clipboard.writeText(returnUrl.value)
  copied.value = true
  setTimeout(() => { copied.value = false }, 2000)
}

// ─────────────────────────────────────────────
// Silence departure transition
// ─────────────────────────────────────────────

function beginSilenceDeparture() {
  if (silenceDeparting.value) return
  silenceDeparting.value = true
  silenceDepartureTimer = setTimeout(() => {
    router.push({ name: 'forge_door' })
  }, 12000)
}

// ─────────────────────────────────────────────
// ActionCable
// ─────────────────────────────────────────────

onMounted(() => {
  fetchSession()

  channel = consumer.subscriptions.create(
    { channel: 'ForgeSessionChannel', session_id: sessionId },
    {
      received(data) {
        if (!data) return

        if (data.forge_message) {
          const msg = data.forge_message
          if (msg.sender === 'seeker') return
          clearResponseTimeout()
          if (seenMessageIds.has(msg.id)) return
          seenMessageIds.add(msg.id)

          messageQueue.value.push({ msg, data })
          processQueue()
        } else if (data.type === 'state_update') {
          if (data.session_state) {
            messageQueue.value.push({ data, isStateUpdate: true })
            processQueue()
          }
        }
      }
    }
  )
})

onUnmounted(() => {
  if (channel) channel.unsubscribe()
  if (departureTimer) clearTimeout(departureTimer)
  if (brokenTimer) clearTimeout(brokenTimer)
  if (silenceDepartureTimer) clearTimeout(silenceDepartureTimer)
  clearResponseTimeout()
})
</script>

<style scoped>
.forge-chamber {
  height: 100vh;
  background-color: #0a0a0a;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chamber-waiting {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chamber-inaccessible {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  gap: 1.5rem;
  text-align: center;
}

.inaccessible-heading {
  color: #a1887f;
  font-style: italic;
  font-size: 1.2rem;
  max-width: 480px;
  line-height: 1.8;
  animation: messageReveal 1.2s ease-out;
}

.inaccessible-body {
  color: rgba(161, 136, 127, 0.5);
  font-style: italic;
  font-size: 0.9rem;
  max-width: 420px;
  line-height: 1.8;
  animation: messageReveal 1.6s ease-out;
}

.inaccessible-btn {
  background: none;
  border: 1px solid rgba(161, 136, 127, 0.2);
  color: rgba(161, 136, 127, 0.6);
  padding: 0.5rem 1.5rem;
  font-size: 0.85rem;
  letter-spacing: 0.05em;
  cursor: pointer;
  border-radius: 2px;
  transition: all 0.3s;
  animation: messageReveal 2s ease-out;
}

.inaccessible-btn:hover {
  border-color: rgba(161, 136, 127, 0.5);
  color: #a1887f;
}

.chamber-sealed {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.sealed-message {
  color: #a1887f;
  font-style: italic;
  font-size: 1.2rem;
  text-align: center;
  max-width: 500px;
  line-height: 1.8;
  animation: messageReveal 1.2s ease-out;
}

.chamber-active {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  max-width: 700px;
  width: 100%;
  margin: 0 auto;
  padding: 1rem;
}

.messages-container {
  flex: 1;
  overflow-y: auto;
  padding: 2rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.messages-fading {
  animation: messagesFade 7s ease-in-out 12s forwards;
}

.forge-message {
  max-width: 85%;
  animation: messageReveal 0.6s ease-out;
}

.forge-message.seeker {
  align-self: flex-end;
}

.forge-message.forge {
  align-self: flex-start;
}

.forge-message.companion_seed {
  align-self: flex-start;
}

.message-content {
  padding: 0.8rem 1.2rem;
  border-radius: 4px;
  line-height: 1.6;
  white-space: pre-line;
  font-size: 1rem;
}

.forge-message.seeker .message-content {
  color: #d7ccc8;
  background-color: rgba(161, 136, 127, 0.1);
  border-left: 2px solid rgba(161, 136, 127, 0.3);
}

.forge-message.forge .message-content {
  color: #bcaaa4;
  background-color: rgba(139, 69, 19, 0.08);
  border-left: 2px solid rgba(139, 69, 19, 0.3);
}

.forge-message.companion_seed .message-content {
  color: #c5cae9;
  background-color: rgba(92, 107, 192, 0.06);
  border-left: 2px solid rgba(92, 107, 192, 0.25);
}

/* Silence footer */
.silence-footer {
  padding: 1.5rem 0 1rem;
  border-top: 1px solid rgba(161, 136, 127, 0.1);
}

.silence-return-prompt {
  color: rgba(161, 136, 127, 0.6);
  font-size: 0.9rem;
  font-style: italic;
  margin-bottom: 0.75rem;
}

.silence-timer {
  color: rgba(161, 136, 127, 0.4);
  font-size: 0.85rem;
  font-style: italic;
  text-align: center;
  padding: 0.75rem 0;
}

.return-link-block {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 0.5rem;
  padding: 0.6rem 0;
  border-top: 1px solid rgba(161, 136, 127, 0.08);
}

.return-link-label {
  color: rgba(161, 136, 127, 0.4);
  font-size: 0.8rem;
  font-style: italic;
}

.return-copy-btn {
  background: none;
  border: 1px solid rgba(161, 136, 127, 0.2);
  color: rgba(161, 136, 127, 0.6);
  padding: 0.3rem 0.8rem;
  font-size: 0.75rem;
  letter-spacing: 0.04em;
  cursor: pointer;
  border-radius: 2px;
  transition: all 0.2s;
  white-space: nowrap;
  flex-shrink: 0;
}

.return-copy-btn:hover {
  border-color: rgba(161, 136, 127, 0.5);
  color: #a1887f;
}

/* Departure hold — input removed, messages visible */
.departure-hold {
  height: 3rem;
}

/* Offering form */
.offering-form {
  display: flex;
  gap: 0.75rem;
  padding: 1rem 0;
  align-items: flex-end;
}

.offering-textarea {
  flex: 1;
  background-color: transparent;
  border: none;
  border-bottom: 1px solid rgba(161, 136, 127, 0.2);
  color: #d7ccc8;
  font-size: 1rem;
  line-height: 1.5;
  padding: 0.5rem;
  resize: none;
  outline: none;
  overflow-y: auto;
  max-height: 40vh;
  transition: border-color 0.3s;
}

.offering-textarea::placeholder {
  color: rgba(161, 136, 127, 0.3);
  font-style: italic;
}

.offering-textarea:focus {
  border-bottom-color: rgba(161, 136, 127, 0.5);
}

.offering-submit {
  background: none;
  border: 1px solid rgba(161, 136, 127, 0.2);
  color: #a1887f;
  padding: 0.5rem 1.2rem;
  font-size: 0.85rem;
  letter-spacing: 0.05em;
  cursor: pointer;
  transition: all 0.3s;
  border-radius: 2px;
  white-space: nowrap;
}

.offering-submit:hover:not(:disabled),
.offering-submit:focus:not(:disabled) {
  border-color: rgba(161, 136, 127, 0.5);
  color: #d7ccc8;
}

.offering-submit:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(161, 136, 127, 0.2);
}

.offering-submit:disabled {
  opacity: 0.2;
  cursor: default;
}

/* Typing indicator */
.typing-indicator {
  display: inline-flex;
  align-items: center;
  padding: 0.8rem 1.2rem;
}

.typing-indicator span {
  height: 6px;
  width: 6px;
  background-color: #a1887f;
  border-radius: 50%;
  display: inline-block;
  margin-right: 4px;
  opacity: 0.4;
  animation: forgeDot 1.4s infinite ease-in-out;
}

.typing-indicator span:nth-child(1) { animation-delay: 0s; }
.typing-indicator span:nth-child(2) { animation-delay: 0.2s; }
.typing-indicator span:nth-child(3) { animation-delay: 0.4s; margin-right: 0; }

/* Pulse indicator */
.forge-pulse {
  width: 8px;
  height: 8px;
  background-color: #a1887f;
  border-radius: 50%;
  margin: 0 auto;
  animation: forgePulse 2s ease-in-out infinite;
}

@keyframes forgeDot {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
  30% { transform: translateY(-3px); opacity: 0.8; }
}

@keyframes forgePulse {
  0%, 100% { opacity: 0.3; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.5); }
}

@keyframes messageReveal {
  0% { opacity: 0; transform: translateY(6px); }
  100% { opacity: 1; transform: translateY(0); }
}

@keyframes messagesFade {
  0% { opacity: 1; }
  100% { opacity: 0; }
}

.messages-container::-webkit-scrollbar { width: 4px; }
.messages-container::-webkit-scrollbar-track { background: transparent; }
.messages-container::-webkit-scrollbar-thumb {
  background-color: rgba(161, 136, 127, 0.2);
  border-radius: 2px;
}
</style>
